import React from "react";
import {
  getScaleName,
  getScaleValueLabel,
  getScaleValues,
  getScorePillProps,
  isDefaultScoringScale,
  normalizeScoringScale,
} from "../../utils/scoringScale";

// The wording that goes with each point of the POMMe-Health scale. It is the
// scale's own definition rather than something the API sends — the built-in
// scales ship without labels — so it stays here, keyed by value.
const POMME_HEALTH_GUIDELINES = [
  {
    value: 1,
    title: "Complete impairment",
    text: "means that a problem that is present more than 95% of the time, with an intensity, which is totally disrupting the person's day to day life and which happens every day over the last 30 days.",
  },
  {
    value: 2,
    title: "Severe impairment",
    text: "means that a problem that is present more than 50% of the time, with an intensity, which is partially disrupting the person's day to day life and which happens frequently over the last 30 days.",
  },
  {
    value: 3,
    title: "Moderate impairment",
    text: "means that a problem that is present less than 50% of the time, with an intensity, which is interfering in the person's day to day life and which happens occasionally over the last 30 days.",
  },
  {
    value: 4,
    title: "Mild impairment",
    text: "means a problem that is present less than 25% of the time, with an intensity a person can tolerate and which happens rarely over the last 30 days.",
  },
  {
    value: 5,
    title: "No impairment",
    text: "means the person has no problem.",
  },
];

// What one heading is scored on, for the "Scoring Guidelines" overlay. The
// POMMe-Health scale keeps the wording it has always shown; any other scale —
// ICF, ten-point, or one an organization defined — is described from what the
// API sends: its name, its values, their colours and any labels.
const ScoringGuidelines = ({ scale }) => {
  const resolved = normalizeScoringScale(scale);
  const name = getScaleName(resolved);

  if (isDefaultScoringScale(resolved)) {
    return (
      <>
        <p className="text-justify">
          POMMe-Health Scoring Scale (default): Note*
        </p>
        {POMME_HEALTH_GUIDELINES.map((guideline) => (
          <p className="text-justify mb-1 text-wrap" key={guideline.value}>
            <span className="font-weight-bolder">
              {guideline.value} {guideline.title}
            </span>{" "}
            {guideline.text}
          </p>
        ))}
        <p className="text-justify mb-0">
          * This is a custom scoring scale, based on the ICF Traditional
          scoring, but the higher functioning is now a higher number.
        </p>
      </>
    );
  }

  return (
    <>
      <p className="text-justify mb-2">
        <span className="font-weight-bolder">{name}</span>
        {resolved?.description ? ` — ${resolved.description}` : ""}
      </p>
      {getScaleValues(resolved).map((value) => {
        const { className, style } = getScorePillProps(resolved, value);
        const label = getScaleValueLabel(resolved, value);
        return (
          <div className="d-flex align-items-center mb-1" key={value}>
            <span
              className={`rounded-pill d-flex align-items-center justify-content-center font-weight-bold ${className}`}
              style={{
                width: "24px",
                height: "24px",
                fontSize: "11px",
                flex: "0 0 auto",
                ...style,
              }}
            >
              {value}
            </span>
            {label && (
              <small className="ml-2 text-justify text-wrap">{label}</small>
            )}
          </div>
        );
      })}
    </>
  );
};

export default ScoringGuidelines;
